import React from 'react';
import { Link } from "react-router-dom";

export default function MenuDocs({ topic, setTopic, schoolType, embedded = false }) {
	const validTopics = ['menu_basics'];
	const menuTopic = embedded ? 'menu_basics' : (validTopics.includes(topic) ? topic : 'default');

	const chooseMenuTopic = (newTopic) => {
		if (menuTopic === newTopic) {
			setTopic(null);
		} else {
			setTopic(newTopic);
		}

		scrollToInstructions('topOfThePage');
	};

	const scrollToInstructions = (sectionId) => {
		const section = document.getElementById(sectionId);
		if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};

	return (
		<div className='documentation-page'>
			<div id='topOfThePage'></div>
			{!embedded &&
				<>
					<p>Use the Magnetboard menu to run global scheduling actions, open reports, and export/print at the right checkpoints.</p>
					<p className='documentation-question-heading'>What would you like help with?</p>
					<div className='choose-topic' style={{ margin: '0px 0px 10px 0px' }}>
						<Link className={`choose-topic-btn ${menuTopic === 'menu_basics' ? 'choose-topic-btn-selected' : ''}`} to={`/app/${schoolType}/documentation/magnetboard/menu/${menuTopic === 'menu_basics' ? 'null' : 'menu_basics'}`} onClick={() => chooseMenuTopic('menu_basics')}>Menu Options</Link>
					</div>
				</>
			}

			{menuTopic === 'menu_basics' &&
			<div className='documentation-container'>
				<h2 className='documentation-page-heading'>Menu Options</h2>
				<div className='documentation-question-heading'>What would you like help with?</div>
				<div className='documentation-container-instruction-option-container'>
					<div className='documentation-container-instruction-option blue-link' onClick={() => scrollToInstructions('menu_open')}>Open and navigate the menu bar</div>
					<div className='documentation-container-instruction-option blue-link' onClick={() => scrollToInstructions('menu_file')}>File options (print/export)</div>
					<div className='documentation-container-instruction-option blue-link' onClick={() => scrollToInstructions('menu_edit')}>Edit options (undo/redo)</div>
					<div className='documentation-container-instruction-option blue-link' onClick={() => scrollToInstructions('menu_actions')}>Run auto-scheduling actions</div>
					<div className='documentation-container-instruction-option blue-link' onClick={() => scrollToInstructions('menu_reports')}>Open reports</div>
				</div>

				<div id='menu_open' className='documentation-container-instruction-container'>
					<div className='documentation-container-instruction-heading'>Open and navigate the menu bar</div>
					<div className='documentation-step-text'>Click a top-level menu name (<span className='turquoise-text'>File</span>, <span className='turquoise-text'>Edit</span>, <span className='turquoise-text'>Actions</span>, or <span className='turquoise-text'>Reports</span>) to open that menu.</div>
					<div className='documentation-step-text'>To close the dropdown, either click the top-level menu name again or just click anywhere outside the dropdown.</div>
					{/* <DocsVideo src={menu_open_video}></DocsVideo> */}
				</div>

				<div id='menu_file' className='documentation-container-instruction-container'>
					<div className='documentation-container-instruction-heading'>File options (print/export)</div>
					<div className='documentation-step-text'>Use <span className='turquoise-text'>File &gt; Print Schedule</span> to print the current Magnetboard view with current filters (including search filters).</div>
					<div className='documentation-step-text'>Use <span className='turquoise-text'>File &gt; Export Schedule</span> when you're ready to export your file back into your SIS. This option requires a paid subscription.</div>
					<div className='documentation-step-text'>If the Export Schedule option is blocked, but you believe you should have access to the export, please contact us at contact@edario.com</div>
					{/* <DocsVideo src={menu_file_video}></DocsVideo> */}
				</div>

				<div id='menu_edit' className='documentation-container-instruction-container'>
					<div className='documentation-container-instruction-heading'>Edit options (undo/redo)</div>
					<div className='documentation-step-text'>Use <span className='turquoise-text'>Edit &gt; Undo</span> and <span className='turquoise-text'>Edit &gt; Redo</span> to step backward or forward through recent placement/edit actions.</div>
					<div className='documentation-step-text'>You can also use th keyboard shortcuts shown in the menu: <span className='turquoise-text'>Ctrl + Z</span> for Undo and <span className='turquoise-text'>Ctrl + Y</span> for Redo.</div>
					{/* <DocsVideo src={menu_edit_video}></DocsVideo> */}
				</div>

				<div id='menu_actions' className='documentation-container-instruction-container'>
					<div className='documentation-container-instruction-heading'>Run auto-scheduling actions</div>
					<div className='documentation-step-text'>Many of the auto-scheduling actions are subscription-only features. If any of these actions are unavailable, but you believe you should have access to them, please contact us at contact@edario.com.</div>
					<div className='documentation-step-text'>In the <span className='turquoise-text'>Actions &gt; Students</span> menu you can:</div>
					<div className='documentation-step-text'>
						<ol>
							<li><span className='turquoise-text'>Clear Students</span>: this action removes all unlocked students from the Magnetboard. If you want to prevent certain students/sections from being cleared, you can always lock them before clearing.</li>
							<li><span className='turquoise-text'>Place Students</span>: this action tries to place any unmatched requests. Usually all students are placed at once, but you can use this to place any remaining unplaced students. For example, if a student comes in late in the scheduling process, you could also use this to auto-place only that one student (since he/she will be the only one with unmatched requests.)</li>
						</ol>
					</div>
					{/* <DocsVideo src={menu_actions_students_video}></DocsVideo> */}
					<div className='documentation-step-text' style={{marginTop:'24px'}}>In the <span className='turquoise-text'>Actions &gt; Classrooms</span> menu you can:</div>
					<div className='documentation-step-text'>
						<ol>
							<li><span className='turquoise-text'>Clear Classrooms</span>: this action removes classroom assignments from sections so you can re-evaluate room usage and rebuild room placements from a cleaner starting point.</li>
						</ol>
					</div>
					{/* <DocsVideo src={menu_actions_classrooms_video}></DocsVideo> */}
					<div className='documentation-step-text' style={{marginTop:'24px'}}>In the <span className='turquoise-text'>Actions &gt; Sections</span> menu you can:</div>
					<div className='documentation-step-text'>
						<ol>
							<li><span className='turquoise-text'>Clear Sections</span>: this action removes current section placements from the board so you can run a fresh section placement pass.</li>
							<li><span className='turquoise-text'>Place Sections</span>: this action auto-places sections into their optimal periods using current scheduling constraints and availability rules.</li>
							<li><span className='turquoise-text'>Lock Sections</span>: in Teacher/Classroom view, this action locks all currently visible sections so they are protected from future auto-moves.</li>
							<li><span className='turquoise-text'>Unlock Sections</span>: in Teacher/Classroom view, this action removes locks from currently visible sections so they can be moved again.</li>
						</ol>
					</div>
					{/* <DocsVideo src={menu_actions_sections_video}></DocsVideo> */}
					<div className='documentation-step-text' style={{marginTop:'24px'}}>In the main <span className='turquoise-text'>Actions</span> dropdown you can:</div>
					<div className='documentation-step-text'>
						<ol>
							<li><span className='turquoise-text'>Create Health Sections</span>: this action opens the PE/Gym-to-Health mapping flow and automatically creates health sections based on that mapping (subscription feature).</li>
						</ol>
					</div>
					{/* <DocsVideo src={menu_actions_create_health_sections_video}></DocsVideo> */}
				</div>

				<div id='menu_reports' className='documentation-container-instruction-container'>
					<div className='documentation-container-instruction-heading'>Open reports</div>
					<div className='documentation-step-text'>Click <span className='turquoise-text'>Reports</span> to launch <span className='turquoise-text'>Schedule Summary</span>, <span className='turquoise-text'>Conflict Matrix</span>, <span className='turquoise-text'>Section Balancing</span>, or <span className='turquoise-text'>Student Summary</span>.</div>
					<div className='documentation-step-text'>It's helpful to use reports as checkpoints after any auto-scheduling feature (for example, after auto-placing sections or auto-placing students) so you can validate the schedule.</div>
					<div className='documentation-step-text'>For more details about reports, visit our <Link className='blue-link' to={`/app/${schoolType}/documentation/magnetboard/reports`}>Reports docs</Link>.</div>
					{/* <DocsVideo src={menu_reports_video}></DocsVideo> */}
				</div>
			</div>
			}
		</div>
	);
}
